import { once, run } from '../../helper/electron';
import { iTrack } from '../../types/ITrack';
import { formatMusicalKey } from '../../utils/formatMusicalKey';

export type LibraryTrackSummary = {
  id: number;
  artist: string;
  title: string;
  bpm?: number;
  key?: string;
  albumCover?: string;
};

export const artworkKey = (artist: string, title: string): string =>
  `${artist.trim().toLowerCase()}|${title.trim().toLowerCase()}`;

export const isCatalogTrackId = (id: string): boolean =>
  /[a-zA-Z]/.test(id) || id.length > 12;

export const isLocalTrackId = (id: string): boolean => /^\d+$/.test(id);

export const albumCoverSrc = (picture: unknown): string | undefined => {
  const asDataUrl = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > 400_000) return undefined;
    if (trimmed.startsWith('data:') || /^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    return `data:image/jpeg;base64,${trimmed}`;
  };

  if (typeof picture === 'string') return asDataUrl(picture);
  if (Array.isArray(picture) && picture[0]) {
    const first = picture[0];
    if (typeof first === 'string') return asDataUrl(first);
    if (first && typeof first === 'object' && 'data' in first) {
      const data = (first as { data?: unknown }).data;
      if (typeof data === 'string') return asDataUrl(data);
    }
  }
  return undefined;
};

export const librarySummaryToTrack = (row: LibraryTrackSummary): iTrack => ({
  ID: String(row.id),
  Artist: row.artist,
  Title: row.title,
  AlbumCover: row.albumCover,
  BPM: row.bpm,
  Key_Camelot: formatMusicalKey(row.key) || undefined,
  Key: formatMusicalKey(row.key) || undefined,
});

export const fetchLibraryTracksByIds = (
  ids: number[],
): Promise<LibraryTrackSummary[]> =>
  new Promise((resolve) => {
    if (ids.length === 0 || typeof window === 'undefined' || !window.api) {
      resolve([]);
      return;
    }

    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const timer = window.setTimeout(() => resolve([]), 8000);
    once(`my-music/get-tracks-by-ids:${requestId}`, (rows: unknown) => {
      window.clearTimeout(timer);
      resolve(Array.isArray(rows) ? (rows as LibraryTrackSummary[]) : []);
    });
    run('my-music/get-tracks-by-ids', { requestId, ids });
  });
